import {Directive, EventEmitter, HostBinding, HostListener, Input, Output} from '@angular/core';
import {Subject} from 'rxjs';
import {UntypedFormGroup} from '@angular/forms';

/**
 * Plumbing commun du mode bandeau (drawer desktop à droite des résultats) pour les
 * deux pages de filtres (recherche et secteurs) : même UI que le mobile, application
 * des filtres en direct via paramsChange (le composant branche liveChange$ avec son
 * debounce) au lieu d'une navigation.
 *
 * Le composant enfant fournit : currentSearch / searchForm / getSearchParams()
 * (mêmes noms dans les deux pages) et storeParams() (search_ad_params vs
 * search_city_params).
 */
@Directive()
export abstract class FiltersDrawerBase<P> {

    @Input() drawer = false;
    @Input() drawerSearchId: string = null;
    @Output() paramsChange = new EventEmitter<P>();
    @Output() drawerClose = new EventEmitter<void>();
    liveChange$ = new Subject<void>();
    protected lastEmittedParams = '';

    // En mode bandeau, la présentation mobile s'applique à tous les viewports
    // (le layout 3 colonnes desktop de la page est scopé sur :host(:not(.drawer-mode)))
    @HostBinding('class.drawer-mode') get drawerMode() {
        return this.drawer;
    }

    abstract currentSearch: any;
    abstract searchForm: UntypedFormGroup;
    abstract getSearchParams(): P;

    /** Écrit les paramètres dans la recherche courante (search_ad_params / search_city_params) */
    protected abstract storeParams(params: P): void;

    // --- Application en direct (mode bandeau) ---
    @HostListener('document:click')
    onDocumentClickLive() {
        if (this.drawer) {
            this.liveChange$.next();
        }
    }

    @HostListener('document:input', ['$event'])
    onDocumentInputLive(event: Event) {
        if (this.drawer && !this.isLiveOnEnterInput(event.target)) {
            this.liveChange$.next();
        }
    }

    // Une sélection au clavier (flèches + Entrée dans un mat-select, slider au clavier...)
    // ne produit ni click ni input : sans ce listener elle ne serait appliquée qu'au
    // prochain clic quelconque
    @HostListener('document:keyup', ['$event'])
    onDocumentKeyupLive(event: KeyboardEvent) {
        if (this.drawer && (!this.isLiveOnEnterInput(event.target) || event.key === 'Enter')) {
            this.liveChange$.next();
        }
    }

    // Champs texte libres (mots clés) : pas de recherche à chaque frappe, application
    // à la touche Entrée ou à la validation (Valider / Rechercher)
    private isLiveOnEnterInput(target: EventTarget): boolean {
        return target instanceof HTMLElement && target.hasAttribute('data-live-on-enter');
    }

    protected emitLiveParams() {
        if (!this.currentSearch || !this.searchForm) {
            return;
        }
        const params = this.getSearchParams();
        const serialized = JSON.stringify(params);
        if (this.lastEmittedParams === '') {
            // Première mesure après le chargement : sert de référence, pas d'émission
            this.lastEmittedParams = serialized;
            return;
        }
        if (serialized !== this.lastEmittedParams) {
            this.lastEmittedParams = serialized;
            this.storeParams(params);
            this.paramsChange.emit(params);
        }
    }

    /**
     * « Rechercher » en mode bandeau : fermeture, et application immédiate SEULEMENT
     * si les filtres ont changé depuis la dernière application en direct (sinon la
     * recherche a déjà été relancée par le live update — pas de requête en double).
     */
    protected drawerSearch(params: P) {
        const serialized = JSON.stringify(params);
        if (serialized !== this.lastEmittedParams) {
            this.lastEmittedParams = serialized;
            this.paramsChange.emit(params);
        }
        this.drawerClose.emit();
    }
}
